<template>
  <div ref="editorWindow" class="gd-editor gd-library-editor-droppable">
    <canvas class="gd-editor-canvas" ref="glRenderCanvas">
    </canvas>
    <EditorNumberEntryPopover ref="numberEntryPopover" />

    <EditorHelpOverlay ref="helpOverlay" v-show="toolHelpText" :text="toolHelpText" :icon="toolHelpIcon" />

    <EditorHelpOverlay ref="viewTypeOverlay" v-show="viewTypeOverlayDisplay" :text="viewTypeOverlayText" :icon="viewTypeOverlayIcon" :showClose="true" @close="closeViewTypeOverlay" />

    <EditorToolSettingsPopover ref="toolSettingsPopover" :editingEnabled="editingEnabled" />

    <EditorSelectionToolPopover ref="selectToolPopover" :editingEnabled="editingEnabled" />

    <EditorShapeToolPopover ref="shapeToolPopover" :editingEnabled="editingEnabled" />

    <EditorEdgeCutoutToolPopover ref="edgeCutoutToolPopover" :editingEnabled="editingEnabled" />

    <EditorCornerCutoutToolPopover ref="cornerCutoutToolPopover" :editingEnabled="editingEnabled" />

    <EditorAddPointsToolPopover ref="addPointsToolPopover" :editingEnabled="editingEnabled" />

    <EditorExpandShrinkToolPopover ref="expandShrinkToolPopover" :editingEnabled="editingEnabled" />

    <EditorCutToolPopover ref="cutToolPopover" :editingEnabled="editingEnabled" />

    <EditorMeasureToolPopover ref="measureToolPopover" :editingEnabled="editingEnabled" />
    
    <EditorSkewToolPopover ref="skewToolPopover" :editingEnabled="editingEnabled" />

    <EditorShapePopover ref="shapePopover" :editingEnabled="editingEnabled" />
    
    <EditorAssemblyPopover ref="assemblyPopover" :editingEnabled="editingEnabled" />

    <EditorDatumPopover ref="datumPopover" :editingEnabled="editingEnabled" />

    <EditorMessagePopover ref="messagePopover" :editingEnabled="editingEnabled" />

    <div v-if="loadingEditor" class="gd-editor-loading-overlay">
      <div class="content">
        Loading Order Editor
        <Spinner />
      </div>
    </div>

  </div>  
</template>

<style lang="scss" scoped>
.gd-editor {
  display: block;
  position: relative;
  background-color: #2D2E32;
  padding: 0;
  margin: 0;
}
.gd-editor-canvas {
  display: block;  
}
.gd-editor-loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: #efefef;
  font-size: 32px;
  color: #404040;
  text-align: center;
}
.gd-editor-loading-overlay > .content {
  position: relative;
  top: 40%;
}
</style>

<script>
import * as THREE from 'three'

import OrderItemMixin from '@/modules/orders/mixins/orderItemMixin'

import EditorNumberEntryPopover from './editorNumberEntryPopover'
import EditorHelpOverlay from './editorHelpOverlay'

import EditorToolSettingsPopover from './tools/editorToolSettingsPopover'
import EditorSelectionToolPopover from './tools/editorSelectionToolPopover'
import EditorShapePopover from './tools/editorShapePopover'
import EditorDatumPopover from './tools/editorDatumPopover'
import EditorShapeToolPopover from './tools/editorShapeToolPopover'
import EditorAssemblyPopover from './tools/editorAssemblyPopover'
import EditorEdgeCutoutToolPopover from './tools/editorEdgeCutoutToolPopover'
import EditorCornerCutoutToolPopover from './tools/editorCornerCutoutToolPopover'
import EditorAddPointsToolPopover from './tools/editorAddPointsToolPopover'
import EditorExpandShrinkToolPopover from './tools/editorExpandShrinkToolPopover'
import EditorCutToolPopover from './tools/editorCutToolPopover'
import EditorMeasureToolPopover from './tools/editorMeasureToolPopover'
import EditorSkewToolPopover from './tools/editorSkewToolPopover'
import EditorMessagePopover from './tools/editorMessagePopover'

import GlassEditor from './../classes/glassEditor'
import RecoveryHelper from './../helpers/recoveryHelper'
import Types from '../types'

export default {
  name: 'Editor',
  mixins: [OrderItemMixin],
  components: {
    EditorNumberEntryPopover,
    EditorHelpOverlay,
    EditorToolSettingsPopover,
    EditorShapePopover,
    EditorAssemblyPopover,
    EditorDatumPopover,
    EditorShapeToolPopover,
    EditorEdgeCutoutToolPopover,
    EditorCornerCutoutToolPopover,
    EditorSelectionToolPopover,
    EditorAddPointsToolPopover,
    EditorExpandShrinkToolPopover,
    EditorCutToolPopover,
    EditorMeasureToolPopover,
    EditorSkewToolPopover,
    EditorMessagePopover
  },
  props: {
    editingEnabled: {
      type: Boolean,
      default: true
    },
    reviewEnabled: {
      type: Boolean,
      default: false
    },
    displayDimensions: {
      type: Boolean,
      default: true,
    },
    displayEdgework: {
      type: Boolean,
      default: true
    },
    displayWarnings: {
      type: Boolean,
      default: true
    },
    snapEnabled: {
      type: Boolean,
      default: true
    },
    snapGridEnabled: {
      type: Boolean,
      default: false
    },

    // Order Info
    isNewOrder: Boolean,
    orderData: Object,
    orderType: String,
    contactId: [Number, String],
    newContactSalesRateId: Number,     // New contacts have a selectable sales rate
    orderItemList: Array
  },
  watch: {
    displayDimensions: function(newVal) {
      this.editor.setOptionDisplayDimensions(newVal)
    },
    displayEdgework: function(newVal) {
      this.editor.setOptionDisplayEdgework(newVal)
    },
    displayWarnings: function(newVal) {
      this.editor.setOptionDisplayWarnings(newVal)
    },
    snapEnabled: function(newVal) {
      this.editor.setOptionSnapEnabled(newVal)
    },
    snapGridEnabled: function(newVal) {
      this.editor.setOptionSnapGridEnabled(newVal)
    },
    editingEnabled: function() {
      if(this.editor)
        this.$data.editor.setEditingAvailable(this.editingEnabled)
    }
  },
  data() {
    return {
      editor: null,
      orderRecoveryHelper: null,
      loadingEditor: false,
      zoomScale: 1.0,
      currentPopover: null,
      currentOverlay: null,
      toolHelpIcon: '',
      toolHelpText: '',
      viewTypeOverlayDisplay: false,
      viewTypeOverlayIcon: '',
      viewTypeOverlayText: '',
    }
  },
  mounted() {
    var w = this.$refs.editorWindow.clientWidth
    var h = this.$refs.editorWindow.clientHeight

    var scene = new THREE.Scene();
    scene.background = new THREE.Color('#fafafa')

    this.$refs.editorWindow.addEventListener('mousewheel', this.onMouseWheel, false)
    this.$refs.editorWindow.addEventListener('mousedown', this.onMouseDown, false)
    this.$refs.editorWindow.addEventListener('mousemove', this.onMouseMove, false)
    this.$refs.editorWindow.addEventListener('mouseup', this.onMouseUp, false)

    this.$refs.editorWindow.addEventListener("touchstart", this.onTouchStart, false)
    this.$refs.editorWindow.addEventListener("touchend", this.onTouchEnd, false)
    this.$refs.editorWindow.addEventListener("touchcancel", this.onTouchCancel, false)
    this.$refs.editorWindow.addEventListener("touchmove", this.onTouchMove, false)

    this.$refs.editorWindow.addEventListener('contextmenu', this.onContextMenu, false)
    window.addEventListener('resize', this.onResize, false)
    window.addEventListener("keydown", this.onKeyDown, false)

    var renderer = new THREE.WebGLRenderer({ canvas: this.$refs.glRenderCanvas, antialias: false })
    renderer.setPixelRatio( window.devicePixelRatio );
    renderer.setSize( w, h );

    this.$data.editor = new GlassEditor(this, renderer, scene, this.$refs.editorWindow)
    this.$data.editor.setEditingAvailable(this.editingEnabled)
    this.$data.editor.setReviewConfigEnabled(this.reviewEnabled)
    this.$data.editor.settings.viewportWidth = w
    this.$data.editor.settings.viewportHeight = h
    this.$data.editor.render()
    this.updateViewport()

    this.$nextTick(() => {
      this.loadingEditor = true
      this.$data.editor.loadEditor()
      .then(() => {
        this.loadingEditor = false
        
        this.onResize()
        this.$data.editor.render()
        this.updateViewport()

        var orderSetupData = {
          type: this.orderType,
          contactId: this.contactId,
          salesRateId: this.newContactSalesRateId,
          orderItems: this.orderItemList
        }
        if(this.orderData) {
          orderSetupData.customerRef = this.orderData.customerRef
          orderSetupData.notes = this.orderData.notes
        }

        this.editor.updateOrder(orderSetupData)

        // If new order, setup order recovery auto-saver
        if(this.isNewOrder && this.orderData) {
          this.orderRecoveryHelper = new RecoveryHelper(this.$data.editor)
          this.orderRecoveryHelper.startRecoveryAutoSaver({ customerRef: this.orderData.customerRef, recoveryUUID: this.orderData.recoveryUUID })
        }

        this.$emit('editorLoaded')
      })
    })
  },
  beforeDestroy() {
    this.$refs.editorWindow.removeEventListener('mousewheel', this.onMouseWheel)
    this.$refs.editorWindow.removeEventListener('mousedown', this.onMouseDown)
    this.$refs.editorWindow.removeEventListener('mousemove', this.onMouseMove)
    this.$refs.editorWindow.removeEventListener('mouseup', this.onMouseUp)
    this.$refs.editorWindow.removeEventListener('touchstart', this.onTouchStart)
    this.$refs.editorWindow.removeEventListener('touchend', this.onTouchEnd)
    this.$refs.editorWindow.removeEventListener('touchcancel', this.onTouchCancel)
    this.$refs.editorWindow.removeEventListener('touchmove', this.onTouchMove)
    this.$refs.editorWindow.removeEventListener('contextmenu', this.onContextMenu)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('keydown', this.onKeyDown)

    if(this.orderRecoveryHelper) {
      this.orderRecoveryHelper.stopRecoveryAutoSaver()
    }

    if(this.$data.editor) {
      this.$data.editor.dispose()
      this.$data.editor = null
    }
  },

  methods: {
    onMouseWheel(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        let mouseData = this.$data.editor.onMouseWheelEvent(ev)
        this.updateViewport(mouseData)
        ev.stopPropagation()
      }
    },

    onMouseDown(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onMouseDownEvent(ev)
        ev.stopPropagation()
      }
    },
    onMouseMove(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        let mouseData = this.$data.editor.onMouseMoveEvent(ev)
        if(ev.buttons != 0)
          this.updateViewport(mouseData)
        else
          this.updateViewData(mouseData)
      }
    },
    onMouseUp(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onMouseUpEvent(ev)
        ev.stopPropagation()
      }
    },

    onTouchStart(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onTouchStartEvent(ev)
        ev.stopPropagation()
        ev.preventDefault()
      }
    },
    onTouchEnd(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onTouchEndEvent(ev)
        ev.stopPropagation()
        ev.preventDefault()
      }
    },
    onTouchCancel(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onTouchCancelEvent(ev)
        ev.stopPropagation()
        ev.preventDefault()
      }
    },
    onTouchMove(ev) {
      if(ev.target == this.$refs.glRenderCanvas) {
        this.$data.editor.onTouchMoveEvent(ev)
        ev.stopPropagation()
        ev.preventDefault()

        this.updateViewData()
      }
    },
    onKeyDown(ev) {
      // Ignore if in a textbox
      switch (ev.target.tagName.toLowerCase()) {
        case 'input':
        case 'textarea':
        case 'select':
          return
      }

      // Popover shortcut keys
      // Escape / Delete / Backspace
      if(ev.code == 'Delete' || ev.code == 'Backspace' || ev.code == 'Escape') {
        if(this.currentPopover) {
          if(this.currentPopover.onShortcutKey)
            this.currentPopover.onShortcutKey(ev.code)
        }
      }

      this.$data.editor.onKeyDownEvent(ev)
    },

    onContextMenu(ev) {
      ev.preventDefault()
      return false
    },
    onResize(ev) {
      this.$data.editor.updateViewport(this.$refs.editorWindow.clientWidth, this.$refs.editorWindow.clientHeight)
      this.updateViewport()
    },

    updateViewport(mouseData = null) {
      let midX = this.$refs.editorWindow.clientWidth / 2
      let midY = this.$refs.editorWindow.clientHeight / 2
      let origin = {
        x: midX - this.$data.editor.originOffsetX,
        y: midY - this.$data.editor.originOffsetY
      }

      this.$emit('viewportUpdated', {
        zoomScale: this.$data.editor.zoomScale,
        origin: origin,
        mousePosition: mouseData ? mouseData.position : null
      })

      // If a scene overlay / popover is displayed, update it to drag with the target location & resize the viewport
      if(this.currentOverlay) {
        this.currentOverlay.viewportUpdated(this.$refs.editorWindow, this.editor)
      }
      if(this.currentPopover) {
        this.currentPopover.viewportUpdated(this.$refs.editorWindow, this.editor)
      }
    },
    updateViewData(mouseData = null) {
      let midX = this.$refs.editorWindow.clientWidth / 2
      let midY = this.$refs.editorWindow.clientHeight / 2
      let origin = {
        x: midX - this.$data.editor.originOffsetX,
        y: midY - this.$data.editor.originOffsetY
      }

      this.$emit('viewDataUpdated', {
        zoomScale: this.$data.editor.zoomScale,
        origin: origin,
        mousePosition: mouseData ? mouseData.position : null
      })
    },

    updateSelectedTool(toolType) {
      this.$emit('selectedToolUpdated', toolType)
    },
    updateToolsEnabled(toolsEnabled) {
      this.$emit('toolsEnabled', toolsEnabled)
    },
    updateUserConfig(userConfigData, themeData) {
      this.$emit('userConfigUpdated', {
        userConfigData: userConfigData,
        themeData: themeData
      })
    },
    updateWarningList(warningList) {
      this.$emit('warningListUpdated', warningList)
    },
    updatePanelReviewCount(panelCount) {
      this.$emit('panelReviewCountUpdated', panelCount)
    },
    updateLibrary() {
      this.$emit('libraryUpdated')
    },


    // Popovers
    displayNumberEntryPopover(options, position, displayOptions) {
      this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.numberEntryPopover
        this.$refs.numberEntryPopover.setup(this.editor, options)
        this.$refs.numberEntryPopover.displayFromWorldPosition(this.$refs.editorWindow, this.editor, position, displayOptions)
      })
    },
    updateNumberEntryPopover(value) {
      this.$refs.numberEntryPopover.updateEntryValue(value)
    },

    displayToolSettingsPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.toolSettingsPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.toolSettingsPopover
        this.$refs.toolSettingsPopover.setup(this.editor, options)
        this.$refs.toolSettingsPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displaySelectionToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.selectToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.selectToolPopover
        this.$refs.selectToolPopover.setup(this.editor, options)
        this.$refs.selectToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },

    displayShapeToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.shapeToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.shapeToolPopover
        this.$refs.shapeToolPopover.setup(this.editor, options)
        this.$refs.shapeToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayEdgeCutoutToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.edgeCutoutToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.edgeCutoutToolPopover
        this.$refs.edgeCutoutToolPopover.setup(this.editor, options)
        this.$refs.edgeCutoutToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayCornerCutoutToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.cornerCutoutToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.cornerCutoutToolPopover
        this.$refs.cornerCutoutToolPopover.setup(this.editor, options)
        this.$refs.cornerCutoutToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayAddPointsToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.addPointsToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.addPointsToolPopover
        this.$refs.addPointsToolPopover.setup(this.editor, options)
        this.$refs.addPointsToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayExpandShrinkToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.expandShrinkToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.expandShrinkToolPopover
        this.$refs.expandShrinkToolPopover.setup(this.editor, options)
        this.$refs.expandShrinkToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayCutToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.cutToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.cutToolPopover
        this.$refs.cutToolPopover.setup(this.editor, options)
        this.$refs.cutToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayMeasureToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.measureToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.measureToolPopover
        this.$refs.measureToolPopover.setup(this.editor, options)
        this.$refs.measureToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displaySkewToolPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.skewToolPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.skewToolPopover
        this.$refs.skewToolPopover.setup(this.editor, options)
        this.$refs.skewToolPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },

    displayShapePopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.shapePopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.shapePopover
        this.$refs.shapePopover.setup(this.editor, options)
        this.$refs.shapePopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },

    displayAssemblyPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.assemblyPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.assemblyPopover
        this.$refs.assemblyPopover.setup(this.editor, options)
        this.$refs.assemblyPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },

    displayDatumPopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.datumPopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.datumPopover
        this.$refs.datumPopover.setup(this.editor, options)
        this.$refs.datumPopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },
    displayMessagePopover(options, displayOptions) {
      if(this.currentPopover != this.$refs.messagePopover)
        this.hidePopover()

      this.$nextTick(() => {
        this.currentPopover = this.$refs.messagePopover
        this.$refs.messagePopover.setup(this.editor, options)
        this.$refs.messagePopover.displayToolPopover(this.$refs.editorWindow, this.editor, displayOptions)
      })
    },

    showToolHelpBox(icon, text) {
      this.toolHelpIcon = icon
      this.toolHelpText = text

      this.$nextTick(() => {
        this.$refs.helpOverlay.adjustCenterToPosition(this.$refs.editorWindow, this.$refs.helpOverlay.$el, this.$refs.editorWindow.offsetWidth / 2, 30)
      })
    },
    closeToolHelpBox() {
      this.toolHelpIcon = ''
      this.toolHelpText = ''
    },

    showViewTypeOverlay(icon, text) {
      this.viewTypeOverlayDisplay = true
      this.viewTypeOverlayIcon = icon
      this.viewTypeOverlayText = text

      this.$nextTick(() => {
        this.$refs.viewTypeOverlay.adjustCenterToPosition(this.$refs.editorWindow, this.$refs.viewTypeOverlay.$el, this.$refs.editorWindow.offsetWidth / 2, 30)
      })
    },
    hideViewTypeOverlay() {
      this.viewTypeOverlayDisplay = false
    },
    closeViewTypeOverlay() {
      this.$emit('revertViewType')
    },


    hidePopover() {
      if(this.currentPopover) {
        this.currentPopover.hide()
        this.currentPopover = null
      }
    },
    updatePopoverErrorText(errorText) {
      if(this.currentPopover) {
        if(this.currentPopover.updateErrorText)
          this.currentPopover.updateErrorText(errorText)
      }
    }
  }
}
</script>